/** @format */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../../extra/Table";
import Button from "../../extra/Button";
import Input from "../../extra/Input";
import ToggleSwitch from "../../extra/ToggleSwitch";
import Pagination from "../../extra/Pagination";
import { setToast } from "../../../util/toast";

const PaidShippingRates = () => {
	const [rates, setRates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [edits, setEdits] = useState({}); // { [rateId]: priceString }
	const [savingId, setSavingId] = useState(null);

	const loadRates = async () => {
		try {
			setLoading(true);
			const res = await axios.get("paidShipping/rates");
			if (res.data?.status) setRates(res.data.rates || []);
			else setToast("error", res.data?.message || "Failed to load rates");
		} catch (e) {
			setToast("error", e.message || "Failed to load rates");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadRates();
	}, []);

	const filtered = useMemo(() => {
		if (!search) return rates;
		const s = search.toLowerCase();
		return rates.filter((r) => (r.state || "").toLowerCase().includes(s) || (r.zone || "").toLowerCase().includes(s));
	}, [rates, search]);

	const handlePriceChange = (id, value) => {
		setEdits((prev) => ({ ...prev, [id]: value }));
	};

	const handleSavePrice = async (row) => {
		const raw = edits[row._id];
		if (raw === undefined) return;
		const price = Number(raw);
		if (!Number.isFinite(price) || price < 0) {
			setToast("error", "Price must be a non-negative number");
			return;
		}
		try {
			setSavingId(row._id);
			const res = await axios.patch(`paidShipping/rate/${row._id}`, { price });
			if (res.data?.status) {
				setRates((prev) => prev.map((r) => (r._id === row._id ? res.data.rate : r)));
				setEdits((prev) => {
					const next = { ...prev };
					delete next[row._id];
					return next;
				});
				setToast("success", "Price updated");
			} else {
				setToast("error", res.data?.message || "Failed to update");
			}
		} catch (e) {
			setToast("error", e.message || "Failed to update");
		} finally {
			setSavingId(null);
		}
	};

	const handleToggleActive = async (row) => {
		try {
			const res = await axios.patch(`paidShipping/rate/${row._id}`, { isActive: !row.isActive });
			if (res.data?.status) {
				setRates((prev) => prev.map((r) => (r._id === row._id ? res.data.rate : r)));
			} else {
				setToast("error", res.data?.message || "Failed to update");
			}
		} catch (e) {
			setToast("error", e.message || "Failed to update");
		}
	};

	const mapData = [
		{
			Header: "No",
			width: "20px",
			Cell: ({ index }) => <span>{page * rowsPerPage + parseInt(index) + 1}</span>,
		},
		{
			Header: "State",
			body: "state",
			sorting: true,
			Cell: ({ row }) => <span className='fw-bold'>{row?.state || "-"}</span>,
		},
		{
			Header: "Zone",
			body: "zone",
			Cell: ({ row }) => <span>{row?.zone || "—"}</span>,
		},
		{
			Header: "Price (₦)",
			body: "price",
			Cell: ({ row }) => {
				const draft = edits[row._id];
				const value = draft !== undefined ? draft : String(row?.price ?? 0);
				return <input type='number' min='0' step='1' value={value} onChange={(e) => handlePriceChange(row._id, e.target.value)} style={{ width: "120px", padding: "6px 10px", borderRadius: "6px", border: "1px solid #ddd", fontSize: "14px" }} />;
			},
		},
		{
			Header: "Active",
			body: "isActive",
			Cell: ({ row }) => (
				<div className='boxCenter'>
					<ToggleSwitch value={row?.isActive} onClick={() => handleToggleActive(row)} />
				</div>
			),
		},
		{
			Header: "Save",
			Cell: ({ row }) => {
				const dirty = edits[row._id] !== undefined && String(edits[row._id]) !== String(row?.price ?? 0);
				return <Button newClass={`whiteFont`} btnName={savingId === row._id ? "Saving…" : "Save"} btnColor={`btnBlackPrime`} style={{ borderRadius: "6px", padding: "6px 14px", opacity: dirty ? 1 : 0.5 }} onClick={() => dirty && handleSavePrice(row)} />;
			},
		},
	];

	return (
		<div className='mainSellerTable'>
			<div className='sellerTable'>
				<div className='col-12 headname'>Paid Shipping Rates</div>
				<div className='sellerMain'>
					<div className='tableMain mt-2'>
						<div className='sellerHeader primeHeader'>
							<div className='row align-items-center'>
								<div className='col-md-6'>
									<input type='text' placeholder='Search by state or zone…' value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px" }} />
								</div>
								<div className='col-md-6 text-end'>
									<span style={{ fontSize: "14px", color: "#666" }}>
										{filtered.length} row{filtered.length !== 1 ? "s" : ""}
									</span>
								</div>
							</div>
							<div className='row mt-2'>
								<div className='col-12'>
									<small style={{ color: "#64748b" }}>Set the per-seller shipping fee for each state. Lagos is split into Mainland and Island. Disable a row to hide Paid Shipping for buyers in that state.</small>
								</div>
							</div>
						</div>
						{loading ? <div className='p-4'>Loading…</div> : <Table data={filtered} mapData={mapData} PerPage={rowsPerPage} Page={page} type={"client"} />}
						<Pagination component='div' count={filtered?.length} serverPage={page} type={"client"} onPageChange={(_e, p) => setPage(p)} serverPerPage={rowsPerPage} totalData={filtered?.length} onRowsPerPageChange={(v) => setRowsPerPage(parseInt(v, 10))} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaidShippingRates;
